/**
 * /admin/redeem-attempts — global operator scan of every invite/redeem
 * attempt across all accounts (OCP-514).
 *
 * Read-only. Same filter-sidebar + table layout as AdminAuditPage. Fetches
 * `/v1/admin/redeem-attempts?outcome=&from=&to=` (newest first) so a spike of
 * `error` / `provision_failed` (systemic problem) or `expired` / `exhausted`
 * (code-lifecycle issue) is visible fleet-wide. Each row links to the acting
 * account's detail page. The per-account slice lives on the account detail.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, ApiError } from '../lib/api';
import { RedeemOutcomePill } from '../components/RedeemOutcomePill';
import { fmtDateTime } from '../lib/format-time';
import { GeoIpAttribution } from '../components/GeoIpAttribution';

interface RedeemAttemptRow {
  id: string;
  account_id: string | null;
  user_email: string;
  entered_code: string;
  matched_code_id: string | null;
  matched_code: string | null;
  outcome: string;
  ip_address: string | null;
  country: string | null;
  user_agent: string | null;
  created_at: string;
}

const OUTCOME_OPTIONS = [
  'success',
  'unknown',
  'expired',
  'exhausted',
  'malformed',
  'error',
  'provision_failed',
  'rejected_duplicate',
  'rejected_input',
] as const;
type OutcomeOption = (typeof OUTCOME_OPTIONS)[number];

interface Filters {
  outcome: OutcomeOption | '';
  from: string;
  to: string;
}

const EMPTY_FILTERS: Filters = { outcome: '', from: '', to: '' };

/** OCP-564 - per-outcome counts in the date window; systemic = error + provision_failed only. */
interface Rollup {
  by_outcome: Record<string, number>;
  systemic_failures: number;
  systemic_outcomes: string[];
}

function fmtUtc(iso: string): string {
  return fmtDateTime(iso);
}

export function AdminRedeemAttemptsPage() {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [items, setItems] = useState<RedeemAttemptRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [rollup, setRollup] = useState<Rollup | null>(null);
  const [error, setError] = useState<string | null>(null);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (filters.outcome) params.set('outcome', filters.outcome);
    // `from`/`to` come from <input type="date"> (YYYY-MM-DD); widen `to` to
    // the end of the chosen day so the window is inclusive of that date.
    if (filters.from) params.set('from', `${filters.from}T00:00:00Z`);
    if (filters.to) params.set('to', `${filters.to}T23:59:59Z`);
    params.set('limit', '200');
    return `?${params.toString()}`;
  }, [filters]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await api<{ items: RedeemAttemptRow[]; total: number; rollup?: Rollup }>(
        `/v1/admin/redeem-attempts${queryString}`,
      );
      setItems(r.items);
      setTotal(r.total);
      setRollup(r.rollup ?? null);
    } catch (err) {
      if (err instanceof ApiError) setError(`Could not load redeem attempts (HTTP ${err.status}).`);
      else setError('Could not load redeem attempts.');
    }
  }, [queryString]);

  useEffect(() => {
    void load();
  }, [load]);

  const hasFilters = Boolean(filters.outcome || filters.from || filters.to);

  return (
    <div className="flex flex-col gap-6 lg:flex-row">
      <aside className="w-full shrink-0 lg:w-56">
        <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Filters</h2>
            {hasFilters && (
              <button
                type="button"
                onClick={() => setFilters(EMPTY_FILTERS)}
                className="text-xs text-brand-500 hover:text-brand-600"
              >
                Reset
              </button>
            )}
          </div>

          <label className="mb-3 flex flex-col gap-1 text-xs">
            <span className="text-gray-600 dark:text-gray-400">Outcome</span>
            <select
              value={filters.outcome}
              onChange={(e) =>
                setFilters((f) => ({ ...f, outcome: e.target.value as OutcomeOption | '' }))
              }
              className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
            >
              <option value="">All</option>
              {OUTCOME_OPTIONS.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </label>

          <label className="mb-3 flex flex-col gap-1 text-xs">
            <span className="text-gray-600 dark:text-gray-400">From (UTC date)</span>
            <input
              type="date"
              value={filters.from}
              onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
              className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
            />
          </label>

          <label className="mb-3 flex flex-col gap-1 text-xs">
            <span className="text-gray-600 dark:text-gray-400">To (UTC date)</span>
            <input
              type="date"
              value={filters.to}
              onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
              className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
            />
          </label>
        </div>
      </aside>

      <main className="min-w-0 flex-1">
        <div className="mb-4">
          <h1 className="text-2xl font-semibold tracking-tight">Redeem attempts</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {items === null
              ? 'Loading…'
              : `${total} ${total === 1 ? 'attempt' : 'attempts'} (newest first)`}
          </p>
          {rollup && (
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400" data-testid="redeem-attempts-rollup">
              In this window:{' '}
              <span className={`font-semibold ${rollup.systemic_failures > 0 ? 'text-red-700 dark:text-red-300' : ''}`}>
                {rollup.systemic_failures} systemic failure{rollup.systemic_failures === 1 ? '' : 's'}
              </span>{' '}
              ({rollup.systemic_outcomes.join(' + ')})
              {Object.entries(rollup.by_outcome)
                .filter(([k]) => !rollup.systemic_outcomes.includes(k))
                .sort()
                .map(([k, v]) => (
                  <span key={k}> · {k} {v}</span>
                ))}
            </p>
          )}
        </div>

        {error && (
          <div
            role="alert"
            className="mb-4 flex items-center justify-between rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
          >
            <span>{error}</span>
            <button
              type="button"
              onClick={() => void load()}
              className="rounded border border-red-300 dark:border-red-700 bg-white dark:bg-gray-900 px-2 py-0.5 text-xs text-red-700 dark:text-red-300 hover:bg-red-100"
            >
              Retry
            </button>
          </div>
        )}

        <RedeemAttemptsTable items={items} />
      </main>
    </div>
  );
}

function RedeemAttemptsTable({ items }: { items: RedeemAttemptRow[] | null }) {
  if (items === null) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-gray-500">
        Loading…
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-gray-500">
        No redeem attempts match the current filters.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
      <table className="w-full min-w-[900px] text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-3 py-2">Time</th>
            <th className="px-3 py-2">Account</th>
            <th className="px-3 py-2">Entered code</th>
            <th className="px-3 py-2">Outcome</th>
            <th className="px-3 py-2">Matched code</th>
            <th className="px-3 py-2">Country</th>
            <th className="px-3 py-2">IP</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
          {items.map((a) => (
            <tr key={a.id}>
              <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-500">
                {fmtUtc(a.created_at)}
              </td>
              <td className="px-3 py-2 text-xs">
                {a.account_id ? (
                  <Link
                    to={`/accounts/${a.account_id}`}
                    className="text-brand-500 hover:text-brand-600"
                    title={a.user_email}
                  >
                    {a.user_email}
                  </Link>
                ) : (
                  <span className="text-gray-500" title="No account on this attempt">
                    {a.user_email || '—'}
                  </span>
                )}
              </td>
              <td className="px-3 py-2 font-mono text-xs">{a.entered_code}</td>
              <td className="px-3 py-2 text-xs">
                <RedeemOutcomePill outcome={a.outcome} />
              </td>
              <td className="px-3 py-2 font-mono text-xs text-gray-500">
                {a.matched_code ?? '—'}
              </td>
              <td className="px-3 py-2 text-xs">{a.country ?? '—'}</td>
              <td className="px-3 py-2 font-mono text-xs text-gray-500">{a.ip_address ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <GeoIpAttribution />
    </div>
  );
}
